import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetRangeSummary } from '@/api/summary/summary';
import { useAddWeight, useGetWeights } from '@/api/weight/weight';
import { useQueryClient } from '@tanstack/react-query';
import { getGetWeightsQueryKey } from '@/api/weight/weight';
import { Button } from '@/components/ui/button';
import { FilterChip } from '@/components/ui/chip';
import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/field';
import { daysAgoParam, kcal, toDateParam } from '@/lib/utils';

const RANGES = [
  { days: 7, label: '7 วัน' },
  { days: 30, label: '30 วัน' },
  { days: 90, label: '90 วัน' },
] as const;

const AXIS = { stroke: 'var(--color-ink-muted)', fontSize: 11 };

export function Trends() {
  const [days, setDays] = useState<number>(30);
  const from = daysAgoParam(days - 1);
  const to = toDateParam(new Date());

  const range = useGetRangeSummary({ from, to });
  const weights = useGetWeights({ from, to });

  return (
    <>
      <div className="flex gap-2">
        {RANGES.map((option) => (
          <FilterChip
            key={option.days}
            selected={option.days === days}
            onClick={() => setDays(option.days)}
            className="flex-1 justify-center"
          >
            {option.label}
          </FilterChip>
        ))}
      </div>

      <Card className="space-y-3">
        <CardTitle>แคลอรีต่อวัน</CardTitle>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={range.data?.days ?? []}>
              <CartesianGrid stroke="var(--color-border-subtle)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} {...AXIS} />
              <YAxis width={40} {...AXIS} />
              <Tooltip
                formatter={(value) => [`${kcal(Number(value))} kcal`, 'กินไป']}
                labelFormatter={(label) => shortDate(String(label))}
              />
              {range.data?.budget != null && (
                // The budget line is the whole point of the chart — without it
                // the bars are just numbers.
                <ReferenceLine
                  y={range.data.budget}
                  stroke="var(--color-brand)"
                  strokeDasharray="4 4"
                />
              )}
              <Bar dataKey="kcal" fill="var(--color-brand)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {range.data && range.data.daysUnderBudget != null && (
          <p className="text-xs text-ink-muted">
            อยู่ในงบ {range.data.daysUnderBudget} จาก {range.data.daysLogged} วันที่บันทึก
          </p>
        )}
      </Card>

      <Card className="space-y-3">
        <CardTitle>น้ำหนัก</CardTitle>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weightSeries(weights.data)}>
              <CartesianGrid stroke="var(--color-border-subtle)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} {...AXIS} />
              <YAxis width={40} domain={['dataMin - 1', 'dataMax + 1']} {...AXIS} />
              <Tooltip
                formatter={(value) => [`${Number(value).toFixed(1)} กก.`, 'น้ำหนัก']}
                labelFormatter={(label) => shortDate(String(label))}
              />
              <Line
                type="monotone"
                dataKey="weightKg"
                stroke="var(--color-brand)"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="linear"
                dataKey="trend"
                stroke="var(--color-ink-muted)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {weights.data?.kgPerWeek != null && (
          <p className="text-xs text-ink-muted">
            แนวโน้ม {weights.data.kgPerWeek > 0 ? '+' : ''}
            {weights.data.kgPerWeek.toFixed(2)} กก./สัปดาห์
          </p>
        )}

        <AddWeight from={from} to={to} />
      </Card>
    </>
  );
}

function AddWeight({ from, to }: { from: string; to: string }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');

  const add = useAddWeight({
    mutation: {
      onSuccess: async () => {
        setValue('');
        await queryClient.invalidateQueries({ queryKey: getGetWeightsQueryKey({ from, to }) });
      },
    },
  });

  const weightKg = Number(value);
  const valid = Number.isFinite(weightKg) && weightKg > 0;

  return (
    <form
      className="flex gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) add.mutate({ data: { weightKg } });
      }}
    >
      <Input
        type="number"
        step="0.1"
        inputMode="decimal"
        placeholder="ชั่งวันนี้ (กก.)"
        aria-label="น้ำหนักวันนี้ กิโลกรัม"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <Button type="submit" disabled={!valid || add.isPending}>
        บันทึก
      </Button>
    </form>
  );
}

/**
 * Merges the weigh-ins and the fitted line into one dataset.
 *
 * The server returns the trend as two endpoints, so only the first and last
 * rows carry a `trend` value and Recharts' `connectNulls` draws the straight
 * line between them — which is exactly what a linear fit is.
 */
function weightSeries(data?: {
  entries: Array<{ weightKg: number; recordedAt: string }>;
  trend: Array<{ date: string; weightKg: number }>;
}) {
  if (!data) return [];

  const trendByDate = new Map(data.trend.map((point) => [point.date, point.weightKg]));

  return data.entries.map((entry) => {
    const date = entry.recordedAt.slice(0, 10);
    return { date, weightKg: entry.weightKg, trend: trendByDate.get(date) ?? null };
  });
}

/** `08-03` — the year is never in doubt over a 90-day window. */
function shortDate(value: string): string {
  return value.slice(5);
}
